import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { RiArrowDownSLine } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import deleteIcon from "../../../assets/admin/delete.svg"
import { sanitizeText } from "../../../utils/TextUtils";
import searchIcon from "../../../assets/admin/search.svg"
import { TiTick } from "react-icons/ti";
import { IoClose } from "react-icons/io5";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    containerVariants,
    itemVariants,
    dropdownVariants,
    buttonAnimations,
    inputAnimations,
    headingVariants,
    sectionVariants,
    loadingButtonAnimations
} from "../../../utils/Animations";
import Loader from "../../../utils/Loader";

const DishAdd = ({ setIsAdd, getDishesData }) => {

    const { rest_slug2 } = useParams();
    const navigate = useNavigate();
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    const [isLoading, setIsLoading] = useState(false);
    const [dishesData, setDishesData] = useState({
        name: "",
        description: "",
    });
    const [logo, setLogo] = useState(null);
    const [selecteLogo, setSelecteLogo] = useState(null);
    const [logoError, setLogoError] = useState("");

    const [showOption, setShowOption] = useState({ allergens: false, course: false });
    const [searchQuery, setSearchQuery] = useState({ allergens: "", course: "" });
    const [allergensData, setAllergensData] = useState([]);
    const [selectedAllergens, setSelectedAllergens] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [courseData, setCourseData] = useState([]);

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
            if (file.size > maxSizeInBytes) {
                setLogoError('Max size: 2MB. Please upload a smaller file.');
                return;
            }
            const img = new Image();
            img.onload = () => {
                setLogoError('');
                const fileUrl = URL.createObjectURL(file);
                setLogo(fileUrl);
                setSelecteLogo(file);
            };
            img.onerror = () => {
                setLogoError('Invalid image file. Please select a valid image.');
            };
            img.src = URL.createObjectURL(file);
        } else {
            setLogoError('No file selected. Please choose a file to upload.');
        }
    };

    const fetchCourseData = async () => {
        try {
            const response = await axios.get(`${apiUrl}/api/v1/restaurant/${rest_slug2}/dish-courses`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { search: searchQuery.course, pagination: 0 },
            });

            setCourseData(response?.data?.data);
        } catch (error) {
            console.error("Error fetching allergens", error);
        }
    };

    const fetchAllergens = async () => {
        try {
            const response = await axios.get(`${apiUrl}/api/v1/get-allergens`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { search: searchQuery.allergens, pagination: 0 },
            });

            setAllergensData(response?.data?.data);
        } catch (error) {
            console.error("Error fetching allergens", error);
        }
    };

    const toggleDropdown = (key) => {
        setShowOption((prev) => ({
            allergens: key === "allergens" ? !prev.allergens : false,
            course: key === "course" ? !prev.course : false,
        }));
    };

    const handleCourseSelection = (courseId) => {
        setSelectedCourse(courseId);
        setTimeout(() => {
            toggleDropdown("course");
        }, 100);
    };

    const handleAllergenSelection = (allergenId) => {
        setSelectedAllergens((prev) =>
            prev.includes(allergenId) ? prev.filter((id) => id !== allergenId) : [...prev, allergenId]
        );
    };

    const handleSave = async () => {
        if (logoError) {
            toast.error(logoError, { theme: "colored", autoClose: 1000 })
            return;
        }
        setIsLoading(true);
        try {
            if (!token) {
                console.error('No token found in localStorage.');
                return;
            }
            const formData = new FormData();
            formData.append("name", dishesData.name);
            if (selectedAllergens.length > 0) {
                selectedAllergens.forEach((allergenId) =>
                    formData.append("allergens[]", allergenId)
                );
            } else {
                formData.append("allergens", "");
            }
            formData.append("description", dishesData.description);
            if (selectedCourse) {
                formData.append("dish_course_id", selectedCourse);
            } else {
                formData.append("dish_course_id", "");
            }
            if (selecteLogo) {
                formData.append('picture', selecteLogo);
            }
            const response = await axios.post(
                `${apiUrl}/api/v1/restaurants/${rest_slug2}/dishes`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            getDishesData();
            toast.success(response?.data?.message || 'Dish added successfully!', {
                theme: 'colored',
                autoClose: 1000,
            });
            setIsAdd(false);
            navigate(`/restaurantPanel/dishManagement/${rest_slug2}`);
        } catch (error) {
            console.error("Error adding dish:", error);
            const errors = error?.response?.data?.errors;
            if (errors) {
                for (const key in errors) {
                    if (Array.isArray(errors[key])) {
                        errors[key].forEach((message) => {
                            toast.error(message, {
                                theme: 'colored',
                                autoClose: 3000,
                            });
                        });
                    }
                }
            } else {
                toast.error(error?.response?.data?.message, {
                    theme: "colored",
                    autoClose: 3000,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setIsAdd(false);
    };

    const deleteProfile = () => {
        setSelecteLogo(null);
        setLogo(null);
    };

    useEffect(() => {
        setSearchQuery({ allergens: "", course: "" });
    }, [showOption]);

    useEffect(() => {
        fetchCourseData();
    }, [searchQuery.course]);

    useEffect(() => {
        fetchAllergens();
    }, [searchQuery.allergens]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (!event.target.closest(".dropdown-container")) {
                setShowOption({ allergens: false, course: false });
            }
        };
        document.addEventListener("click", handleOutsideClick);
        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, []);

    return (
        <motion.section
            initial={sectionVariants.initial}
            animate={sectionVariants.animate}
            transition={sectionVariants.transition}
            className="border-t border-[#D9D9D9] sm:mt-5 mt-0 pt-10"
        >
            <motion.h3
                initial={headingVariants.initial}
                animate={headingVariants.animate}
                transition={headingVariants.transition}
                className="text-2xl font-bold mb-2 poppins-medium"
            >
                Aggiungi Piatto
            </motion.h3>
            <motion.div
                variants={containerVariants}
                className="w-full h-auto bg-[#FCFCFC] border-[2px] border-[#8B24332B] md:p-10 p-4 lg:px-10 md:px-2 rounded-xl shadow-md font-sans space-y-5"
            >
                <motion.div className="lg:flex justify-between gap-3" variants={containerVariants}>
                    <motion.div
                        className="flex lg:w-[75%] w-full flex-col space-y-4 mb-4"
                        variants={containerVariants}
                    >
                        {/* Dish Name and Image */}
                        <motion.div className="flex md:flex-row flex-col w-full gap-6" variants={itemVariants}>
                            <div className="flex flex-col md:w-[50%] w-full space-y-3">
                                <label className="text-sm font-semibold poppins-medium text-[#404040]">
                                    Nome<span className="text-red-500"> *</span>
                                </label>
                                <motion.input
                                    type="text"
                                    placeholder="Nome"
                                    className="poppins-regular px-4 py-3 text-xs text-[#8B8B8B] bg-[#fff] rounded-3xl border border-gray-300 focus:outline-none focus:border-[#d5d6d9] focus:bg-[#f6efef]"
                                    value={sanitizeText(dishesData.name)}
                                    onChange={(e) => setDishesData({ ...dishesData, name: e.target.value })}
                                    {...inputAnimations}
                                />
                            </div>
                            <div className="flex lg:flex-row flex-col gap-x-3  justify-between md:w-[50%] w-full space-y-3">
                                <div className="flex flex-col w-full space-y-3">
                                    <label className="text-sm font-semibold poppins-medium text-[#404040]">
                                        Immagine: <span className="font-normal"> Dim. Massima: 800*600</span>
                                    </label>
                                    <motion.div
                                        className="file-input poppins-regular px-4 py-[9px] text-[12px] text-[#8B8B8B] rounded-[50px] bg-[#fff] border border-gray-300 focus:outline-none focus:border-gray-400 h-[42px] focus:bg-[#e2dbdb]"
                                        {...inputAnimations}
                                    >  <input type="file" onChange={handleLogoChange} accept="image/*" />
                                        <span className="button">Scegli</span>
                                        <span className="label" data-js-label>No file selected</span>
                                    </motion.div>
                                    <AnimatePresence>
                                        {logoError && (
                                            <motion.div
                                                className="text-xs text-red-500 mt-1"
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                            >
                                                {logoError}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                                {/* mobile view */}
                                <AnimatePresence>
                                    {logo && !logoError && (
                                        <motion.div
                                            className="relative flex items-center justify-end lg:hidden"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                        >
                                            <motion.div
                                                className="absolute top-[15px] right-[17px] delete self-end bg-[#8b2433] w-[30px] h-[30px] rounded-[50%] flex items-center justify-center p-[7px] cursor-pointer"
                                                {...buttonAnimations}
                                                onClick={deleteProfile}
                                            >
                                                <img
                                                    className="invert"
                                                    src={deleteIcon}
                                                    height={17}
                                                    width={17}
                                                    alt="Delete"
                                                />
                                            </motion.div>
                                            <div className="overflow-hidden h-full md:w-[180px] w-full flex items-center justify-center border border-[#707070] rounded-[28px] p-[10px]">
                                                <motion.img
                                                    src={logo}
                                                    alt="Preview"
                                                    className="w-full object-cover rounded-lg shadow-md"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ duration: 0.3 }}
                                                />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                        {/* Allergies, Options, Prices, and Description */}
                        <motion.div className="flex gap-6 md:flex-row flex-col" variants={itemVariants}>
                            <div className="flex flex-col md:w-[50%] w-full gap-3">
                                <label className="text-sm font-semibold poppins-medium text-[#6B6B6B]">
                                    Allergeni
                                </label>
                                <div className="flex flex-col w-full space-y-3 dropdown-container">
                                    <div className="relative group w-full">
                                        <motion.button
                                            className={`${showOption.allergens ? "border-b-0 rounded-tl-3xl rounded-tr-3xl" : "rounded-full"} border bg-[#fff] font-medium border-[#d5d6d9] inline-flex justify-between items-center w-full px-4 py-[8px] outline-none shadow-sm relative z-30 text-xs text-[#8B8B8B]`}
                                            onClick={() => toggleDropdown("allergens")}
                                            {...buttonAnimations}
                                        >
                                            <span className="mr-2 flex items-center gap-3 poppins-medium">
                                                {selectedAllergens.length > 0
                                                    ? allergensData
                                                        .filter((allergen) => selectedAllergens.includes(allergen.id))
                                                        .map((allergen) => allergen.name_it)
                                                        .join(", ")
                                                    : "Allergeni"}
                                            </span>
                                            <RiArrowDownSLine
                                                className={`transform transition-transform duration-300 ${showOption.allergens ? "rotate-180" : ""}`}
                                                size={"24px"}
                                                color="#9f414f"
                                            />
                                        </motion.button>
                                        <AnimatePresence>
                                            {showOption.allergens && (
                                                <motion.div
                                                    className="bg-[#FFFFFF] opacity-100 z-50 border-[#d5d6d9] absolute pt-[9px] rounded-t-none border-t-0 p-2 px-4 right-0 mt-0 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 w-full border"
                                                    variants={dropdownVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                    exit="exit"
                                                >
                                                    <motion.div
                                                        className="flex px-[10px] py-[5px] flex-row relative border rounded-[28px] bg-[#f6efef66] my-[5px] justify-around gap-x-2"
                                                        variants={itemVariants}
                                                    >
                                                        <img
                                                            src={searchIcon}
                                                            className="block left-0 top-0 bottom-0 my-auto h-4 w-4"
                                                            alt="Search"
                                                        />
                                                        <input
                                                            type="text"
                                                            className="block poppins-regular bg-transparent outline-none w-full placeholder:text-sm"
                                                            placeholder="Allergeni"
                                                            value={searchQuery.allergens}
                                                            onChange={(e) =>
                                                                setSearchQuery((prev) => ({
                                                                    ...prev,
                                                                    allergens: e.target.value,
                                                                }))
                                                            }
                                                        />
                                                    </motion.div>
                                                    <div className="max-h-[130px] overflow-y-auto btn-all-yscroll">
                                                        {allergensData?.length > 0 ? (
                                                            allergensData.map((allergen, index) => (
                                                                <motion.div
                                                                    className="flex gap-[20px]"
                                                                    key={allergen.id}
                                                                    variants={itemVariants}
                                                                    custom={index}
                                                                >
                                                                    <label className="px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn">
                                                                        <input
                                                                            type="checkbox"
                                                                            value={allergen.id}
                                                                            className="outline-none cursor-pointer"
                                                                            checked={selectedAllergens.includes(allergen.id)}
                                                                            onChange={() => handleAllergenSelection(allergen.id)}
                                                                        />
                                                                    </label>
                                                                    <span className="text-xs text-[#8B8B8B] rounded-md flex items-center poppins-regular">
                                                                        {allergen.name_it}
                                                                    </span>
                                                                </motion.div>
                                                            ))
                                                        ) : (
                                                            <motion.div className="flex gap-[20px] mx-5 my-2" variants={itemVariants}>
                                                                <span className="text-xs text-[#8B8B8B] rounded-md flex items-center poppins-regular">
                                                                    No Allergens are available
                                                                </span>
                                                            </motion.div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col md:w-[50%] w-full gap-3">
                                <label className="text-sm font-semibold poppins-medium text-[#6B6B6B]">
                                    Portata<span className="text-red-500"> *</span>
                                </label>
                                <div className="flex flex-col w-full space-y-3 dropdown-container">
                                    <div className="relative group w-full">
                                        <motion.button
                                            className={`${showOption.course ? "border-b-0 rounded-tl-3xl rounded-tr-3xl" : "rounded-full"} border bg-[#fff] font-medium  border-[#d5d6d9] inline-flex justify-between items-center w-full px-4 py-[8px] outline-none shadow-sm relative z-30 text-xs text-[#8B8B8B]`}
                                            onClick={() => toggleDropdown("course")}
                                            {...buttonAnimations}
                                        >
                                            <span className="mr-2 flex items-center gap-3 poppins-medium">
                                                {selectedCourse && courseData?.length
                                                    ? courseData.find((item) => item.id === selectedCourse)?.name || "Portata"
                                                    : "Portata"}
                                            </span>
                                            <RiArrowDownSLine
                                                className={`transform transition-transform duration-300 ${showOption.course ? "rotate-180" : ""}`}
                                                size={"24px"}
                                                color="#9f414f"
                                            />
                                        </motion.button>
                                        <AnimatePresence>
                                            {showOption.course && (
                                                <motion.div
                                                    className="bg-[#FFFFFF] opacity-100 z-50 border-[#d5d6d9] absolute pt-[9px] rounded-t-none border-t-0 p-2 px-4 right-0 mt-0 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 w-full border"
                                                    variants={dropdownVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                    exit="exit"
                                                >
                                                    <motion.div
                                                        className="flex px-[10px] py-[5px] flex-row relative border rounded-[28px] bg-[#f6efef66] my-[5px] justify-around gap-x-2"
                                                        variants={itemVariants}
                                                    >
                                                        <img
                                                            src={searchIcon}
                                                            className="block left-0 top-0 bottom-0 my-auto h-4 w-4"
                                                            alt="Search"
                                                        />
                                                        <input
                                                            type="text"
                                                            className="block poppins-regular bg-transparent outline-none w-full placeholder:text-sm"
                                                            placeholder="Portata"
                                                            value={searchQuery.course}
                                                            onChange={(e) =>
                                                                setSearchQuery((prev) => ({
                                                                    ...prev,
                                                                    course: e.target.value,
                                                                }))
                                                            }
                                                        />
                                                    </motion.div>
                                                    <div className="max-h-[130px] overflow-y-auto btn-all-yscroll">
                                                        {courseData?.length > 0 ? (
                                                            courseData?.map((option, index) => (
                                                                <motion.div
                                                                    className="flex gap-[20px]"
                                                                    key={option?.id}
                                                                    variants={itemVariants}
                                                                    custom={index}
                                                                >
                                                                    <label className="px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn">
                                                                        <input
                                                                            type="checkbox"
                                                                            value={option?.id}
                                                                            className="outline-none cursor-pointer"
                                                                            checked={selectedCourse === option?.id}
                                                                            onChange={() => handleCourseSelection(option?.id)}
                                                                        />
                                                                    </label>
                                                                    <span className="text-xs text-[#8B8B8B] rounded-md flex items-center poppins-regular">
                                                                        {option?.name}
                                                                    </span>
                                                                </motion.div>
                                                            ))
                                                        ) : (
                                                            <motion.div className="flex gap-[20px] mx-5 my-2" variants={itemVariants}>
                                                                <span className="text-xs text-[#8B8B8B] rounded-md flex items-center poppins-regular">
                                                                    No Allergens are available
                                                                </span>
                                                            </motion.div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div className="flex flex-col w-full gap-3" variants={itemVariants}>
                            <div className="flex flex-col w-full gap-3">
                                <label className="text-sm font-semibold poppins-medium text-[#6B6B6B]">
                                    Descrizione
                                </label>
                                <motion.textarea
                                    rows="4"
                                    placeholder="Descrizione"
                                    className="poppins-regular px-4 py-3 mt-[12px] text-xs text-justify text-[#8B8B8B] rounded-3xl bg-[#fff] border border-gray-300 focus:outline-none focus:border-[#d5d6d9] focus:bg-[#f6efef]"
                                    value={sanitizeText(dishesData.description)}
                                    onChange={(e) => setDishesData({ ...dishesData, description: e.target.value })}
                                    {...inputAnimations}
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                    {/* Desktop view for logo preview */}
                    <motion.div className="flex items-baseline justify-center w-[25%]" variants={itemVariants}>
                        <AnimatePresence>
                            {logo && !logoError && (
                                <motion.div
                                    className="relative lg:block hidden items-center justify-end w-full"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                >
                                    <motion.div
                                        className="absolute top-[15px] right-[17px] delete self-end bg-[#8b2433] w-[30px] h-[30px] rounded-[50%] flex items-center justify-center p-[7px] cursor-pointer"
                                        {...buttonAnimations}
                                        onClick={deleteProfile}
                                    >
                                        <img
                                            className="invert"
                                            src={deleteIcon}
                                            height={17}
                                            width={17}
                                            alt="Delete"
                                        />
                                    </motion.div>
                                    <div className="overflow-hidden h-full md:w-[256px] w-full flex items-center justify-center border border-[#707070] rounded-[28px] p-[10px]">
                                        <motion.img
                                            src={logo}
                                            alt="Preview"
                                            className="w-full object-cover rounded-lg shadow-md"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </motion.div>
                <motion.div className="flex sm:flex-row flex-col text-sm justify-start gap-4 mt-4" variants={itemVariants}>
                    {isLoading ? (
                        <motion.button
                            disabled
                            type="button"
                            className="sm:w-[120px] w-full poppins-regular justify-center flex items-center space-x-2 p-1 px-3 bg-[#8A2533] text-white border-[1.5px] border-[#d18d97] rounded-lg transition-colors"
                            {...loadingButtonAnimations}
                        >
                            <Loader />
                            Loading...
                        </motion.button>
                    ) : (
                        <motion.button
                            className="sm:w-[120px] w-full justify-center flex items-center space-x-2 p-1 px-3 bg-[#FCFCFC] border-[1.5px] border-[#d18d97] text-red-700 rounded-lg hover:text-gray-50 hover:bg-[#8A2533] transition-colors"
                            onClick={handleSave}
                            disabled={isLoading}
                            {...buttonAnimations}
                        >
                            <TiTick size={"25px"} color="green" />
                            <span className="poppins-regular">Salva</span>
                        </motion.button>
                    )}
                    <motion.button
                        className="sm:w-[120px] w-full justify-center flex items-center space-x-2 p-1 px-3 bg-[#FCFCFC] border-[1.5px] border-[#d18d97] text-red-700 rounded-lg hover:text-gray-50 hover:bg-[#8A2533] transition-colors group"
                        onClick={handleCancel}
                        {...buttonAnimations}
                    >
                        <IoClose
                            size={"25px"}
                            className="text-red-700 group-hover:text-white transition-colors"
                        />
                        <span className="poppins-regular">Annulla</span>
                    </motion.button>
                </motion.div>
            </motion.div>
        </motion.section>
    );
};

DishAdd.propTypes = {
    setIsAdd: PropTypes.func.isRequired,
    getDishData: PropTypes.func.isRequired,
};

export default DishAdd;